import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { RiCloseLine, RiMapPin2Line, RiHashtag, RiTruckLine } from "react-icons/ri";
import { getOrderById } from "UpdatedFeatures/api";

const badgeColors = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-yellow-100 text-yellow-700",
  "bg-purple-100 text-purple-700",
  "bg-pink-100 text-pink-700",
];

const ProductDetailsModal = ({ isOpen, setIsOpen, closeModal, order }) => {
  const {
    data: orderDetails,
    isLoading: isLoadingOrder,
    isError: isErrorOrder,
    error: orderError
  } = useQuery({
    queryKey: ['order', order?.id],
    queryFn: () => getOrderById({ orderId: order?.id }),
    enabled: !!order?.id
  });

  const displayOrder = orderDetails || order || {};
  const productsArray = useMemo(() => {
    return Object.values(displayOrder.products || {});
  }, [displayOrder.products]);

  const totalFeeStockPlus = useMemo(() => {
    return productsArray.reduce((sum, product) => {
      const fee = parseFloat(product.fee_stock_plus) || 0;
      return sum + fee;
    }, 0);
  }, [productsArray]);

  if (!isOpen) return null;
  console.log(displayOrder)
  // Pick a badge color for shipping mode
  const shippingBadge = badgeColors[displayOrder.shipping_mode_id % badgeColors.length] || badgeColors[0];


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ zIndex: 9999, overflow: "visible" }}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay with blur for glassmorphism */}
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm transition-opacity duration-300"
        aria-hidden="true"
        onClick={closeModal}
      />
      <div className="relative z-50 flex items-center justify-center w-full h-full p-4">
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-2xl w-full max-w-3xl md:max-w-4xl p-0 relative max-h-[90vh] overflow-y-auto border border-gray-200 transition-all duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#b269ae] to-[#7f53ac] p-6 w-full flex justify-between items-center rounded-t-2xl sticky top-0 z-10 shadow-md">
            <h1 className="text-left text-2xl text-white font-extrabold tracking-tight w-full">
              Détails de la Commande
            </h1>
            <button
              onClick={closeModal}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 focus:outline-none focus:ring-2 focus:ring-white transition"
              aria-label="Fermer le modal"
            >
              <RiCloseLine className="text-3xl text-white" />
            </button>
          </div>

          {/* Loading & Error States */}
          {isLoadingOrder && (
            <div className="flex justify-center items-center py-16">
              <span className="loader border-4 border-[#b269ae] border-t-transparent rounded-full w-12 h-12 animate-spin mr-3" />
              <span className="text-[#b269ae] font-semibold text-lg">Chargement...</span>
            </div>
          )}
          {isErrorOrder && (
            <div className="flex flex-col items-center py-16 text-red-600">
              <span className="font-semibold text-lg">Erreur lors du chargement de la commande.</span>
              <span className="text-sm mt-2">{orderError?.message || 'Veuillez réessayer.'}</span>
            </div>
          )}

          {/* Main Content */}
          {!isLoadingOrder && !isErrorOrder && (
            <div className="p-6 md:p-10">
              <div className="space-y-10">
                {/* Order Info */}
                <div className="rounded-2xl px-4 py-6 flex flex-col md:flex-row md:justify-between md:space-x-12 border-b border-gray-200 pt-4 bg-white/60 shadow-sm">
                  <div className="mb-4 md:mb-0 flex items-start gap-2">
                    <RiMapPin2Line className="text-xl text-[#b269ae]" />
                    <div>
                      <dt className="font-semibold text-gray-900 text-base">Adresse</dt>
                      <dd className="mt-1 text-gray-700 break-words max-w-xs text-sm">{displayOrder.delivery_address}</dd>
                    </div>
                  </div>
                  <div className="mb-4 md:mb-0 flex items-start gap-2">
                    <RiHashtag className="text-xl text-[#b269ae]" />
                    <div>
                      <dt className="font-semibold text-gray-900 text-base">Commande</dt>
                      <dd className="mt-1 text-gray-700 text-sm">{displayOrder.id}</dd>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <RiTruckLine className="text-xl text-[#b269ae]" />
                    <div>
                      <dt className="font-semibold text-gray-900 text-base">Expédition</dt>
                      <dd className="mt-1">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${shippingBadge}`}>{displayOrder.shipping_mode_id}</span>
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                <div className="overflow-x-auto">
                  <table className="mt-4 w-full text-gray-600 sm:mt-6">
                    <thead className="text-left text-xs md:text-sm text-gray-500 bg-white/70">
                      <tr>
                        <th className="py-3 pr-2 font-semibold text-base md:text-lg px-4 w-2/5">Produit</th>
                        <th className="hidden md:table-cell w-1/5 py-3 pr-2 font-semibold text-base md:text-lg">Prix</th>
                        <th className="hidden md:table-cell py-3 pr-2 font-semibold text-base md:text-lg">Quantité</th>
                        <th className="py-3 pr-2 font-semibold text-base md:text-lg">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 border-b border-gray-200 text-sm">
                      {productsArray.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-8 text-center text-gray-400">Aucun produit trouvé.</td>
                        </tr>
                      ) : (
                        productsArray.map((prod, index) => (
                          <tr key={index} className="transition hover:bg-[#f7eafd]">
                            <td className="py-6 pr-8">
                              <div className="flex items-center gap-4">
                                <img
                                  src={prod.image ? `https://api.kdmarche.com/${prod.image}` : '/public/favicon.png'}
                                  alt="product image"
                                  className="h-16 w-16 md:h-20 md:w-20 rounded-xl object-cover object-center border shadow-sm bg-white"
                                />
                                <div>
                                  <div className="font-medium text-gray-900 text-base md:text-lg">{prod.product_title}</div>
                                </div>
                              </div>
                            </td>
                            <td className="hidden md:table-cell py-6 pr-8 align-middle">{prod.price_ht}</td>
                            <td className="hidden md:table-cell py-6 pr-8 align-middle">{prod.quantity}</td>
                            <td className="py-6 pr-8 whitespace-nowrap align-middle font-semibold">{prod.total_ttc}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Order Summary */}
                <div className="mt-8 bg-white/70 rounded-2xl p-6 shadow-lg max-w-lg mx-auto">
                  <dl className="space-y-4">
                    <div className="flex justify-between items-center">
                      <dt className="font-medium text-md text-gray-900">Sous-Total</dt>
                      <dd className="text-md font-medium text-gray-900">€{displayOrder.products_ht}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="font-medium text-md text-gray-900">Total Taxes</dt>
                      <dd className="text-md font-medium text-gray-900">€{displayOrder.products_tax}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="font-medium text-md text-gray-900">Frais de Livraison</dt>
                      <dd className="text-md font-medium text-gray-900">€{displayOrder.shipping_price}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="font-medium text-md text-gray-900">Frais Stock+</dt>
                      <dd className="text-md font-medium text-gray-900">€{totalFeeStockPlus}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="font-medium text-md text-gray-900">Réduction</dt>
                      <dd className="text-md font-medium text-gray-900">€{displayOrder.disount_value || 0}</dd>
                    </div>
                    <div className="flex justify-between items-center border-t pt-4">
                      <dt className="font-semibold text-md text-gray-900">Total</dt>
                      <dd className="text-md font-semibold text-gray-900">€{(Number(displayOrder.total_ttc || 0) + totalFeeStockPlus)}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;
